import { type FORM_VALUES } from "./_data"

import { alpha, styled } from "@mui/material/styles"
import type { UseControllerProps } from "react-hook-form"

export type InputProps = Pick<UseControllerProps<FORM_VALUES>, "control">

export const InputContainer = styled("div")<{ focused: boolean; variant?: string }>(
  ({ focused = false, variant, theme }) => ({
    "alignItems": "center",
    "borderBottom": "2px solid transparent",
    "cursor": "pointer",
    "display": "flex",
    "gridGap": theme.spacing(6),
    "minHeight": 112,
    "padding": theme.spacing(0, 6),

    ...(focused && {
      backgroundColor: theme.palette["secondary.200"].main,
      outline: 0,

      label: {
        color: theme.palette["secondary.800"].main,
      },

      input: {
        "color": theme.palette["secondary.800"].main,
        "&::placeholder": {
          color: theme.palette["secondary.800"].main,
        },
      },
    }),

    ...(variant === "home" && {
      minHeight: 78,
    }),

    [theme.breakpoints.down("md")]: {
      border: `1px solid ${theme.palette["neutral.200"].main}`,
      borderRadius: theme.spacing(1.5),
      boxShadow: `0px 1px 2px 0px ${alpha(theme.palette["neutral.900"].main, 0.06)}`,
      gridGap: theme.spacing(4),
      padding: theme.spacing(4),
      minHeight: 76,

      ...(focused && {
        border: `1px solid ${alpha(theme.palette["secondary.800"].main, 0.1)}`,
      }),
    },

    "[placeholder]": {
      textOverflow: "ellipsis",
    },
  })
)

export const InputIcon = styled("div")(({ theme }) => ({
  cursor: "pointer",
  flexShrink: 0,
  opacity: 0.7,
}))

export const InputControl = styled("div")<{ variant?: string }>(({ variant, theme }) => ({
  cursor: "pointer",
  display: "flex",
  flexFlow: "column",
  flex: "1",
  gridGap: theme.spacing(3),
  marginRight: "auto",
  ...(variant === "home" && {
    "gridGap": 0,
    "& label, input": {
      fontSize: "15px!important",
    },
  }),
}))

export const InputLabel = styled("label")(({ theme }) => ({
  ...theme.typography["body.md"],
  letterSpacing: 0,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
}))

export const Input = styled("input")(({ theme }) => ({
  ...theme.typography["body.lg"],
  "letterSpacing": 0,
  "background": "transparent",
  "border": 0,
  "margin": 0,
  "padding": 0,
  "width": "100%",

  "&:focus": {
    outline: "none",
  },

  [theme.breakpoints.down("md")]: {
    fontSize: 16,
  },
}))

type InputClearBtnProps = {
  focused?: boolean
}
export const InputClearBtn = styled("button")<InputClearBtnProps>(({ focused = false, theme }) => ({
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "flex-end",
  "gridGap": theme.spacing(2),
  "background": focused ? theme.palette["secondary.300"].main : "#F5F6F8",
  "color": `${alpha(theme.palette[focused ? "secondary.800" : "neutral.700"].main, 0.7)}`,
  "borderRadius": 9999,
  "border": 0,
  "cursor": "pointer",
  "flexShrink": 0,
  "margin": 0,
  "maxHeight": 32,
  "overflow": "hidden",
  "padding": theme.spacing(2, 0),
  "position": "relative",
  "height": 32,
  "width": 32,
  "transition": theme.transitions.create([
    "background-color",
    "color",
    "padding",
    "stroke",
    "width",
  ]),

  "& > span": {
    position: "absolute",
    top: "50%",
    right: 33,
    transform: "translate(0, -50%)",
  },

  "& > svg": {
    position: "absolute",
    stroke: theme.palette[focused ? "secondary.800" : "neutral.700"].main,
    strokeWidth: 4,
    width: 10,
    height: 10,
    top: "50%",
    right: 12,
    transform: "translate(0, -50%)",
    opacity: 0.7,
  },

  "&:hover, &:focus": {
    "background": theme.palette[focused ? "secondary.300" : "secondary.200"].main,
    "color": theme.palette["secondary.800"].main,
    "padding": theme.spacing(2, 7, 2, 3),
    "width": 80,

    "& > svg": {
      stroke: theme.palette[focused ? "secondary.800" : "neutral.700"].main,
      color: theme.palette["secondary.800"].main,
    },
  },
}))

export const AutocompletePopover = styled("div")(({ theme }) => ({
  background: theme.palette["neutral.100"].main,
  borderRadius: theme.spacing(1.5),
  boxShadow: `0 4px 6px -1px ${alpha(theme.palette["neutral.900"].main, 0.04)}, 
    0 2px 14px -1px ${alpha(theme.palette["neutral.900"].main, 0.04)}`,
  position: "relative",
  padding: theme.spacing(6, 3, 3),
  zIndex: 1,

  [theme.breakpoints.down("md")]: {
    border: `1px solid ${theme.palette["neutral.200"].main}`,
    maxHeight: "50vh",
    overflow: "auto",
  },
}))

export const ListTitle = styled("div")(({ theme }) => ({
  ...theme.typography["body.sm"],
  letterSpacing: 0,
  color: theme.palette["neutral.700"].main,
  cursor: "default",
  borderBottom: `1px solid ${theme.palette["neutral.200"].main}`,
  paddingBottom: theme.spacing(4),
}))

type ListItemProps = {
  selected?: boolean
  disabled?: boolean
}

export const ListItem = styled("div")<ListItemProps>(
  ({ selected = false, disabled = false, theme }) => ({
    ...theme.typography["body.lg"],
    letterSpacing: 0,
    color: theme.palette["neutral.700"].main,
    backgroundColor: selected ? theme.palette["secondary.200"].main : "transparent",
    borderRadius: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gridGap: theme.spacing(3),
    padding: theme.spacing(3, 2),
    border: "none",
    transition: theme.transitions.create(["background-color", "color"], {
      duration: theme.transitions.duration.short,
    }),

    svg: {
      opacity: 0.7,
    },

    ...(!disabled && {
      "&:hover, &:focus": {
        background: theme.palette["secondary.200"].main,
        color: theme.palette["secondary.800"].main,
        outline: 0,
        borderBottom: "none",

        svg: {
          fill: theme.palette["secondary.800"].main,
        },
      },
    }),

    ...(disabled && {
      "opacity": 0.5,
      "cursor": "not-allowed",

      "> *": {
        opacity: 0.5,
      },
    }),
  })
)

export const LocationListItem = styled(ListItem)(({ theme }) => ({
  ".list-item__icon": {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    height: 20,
    width: 20,
  },
}))
